import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { GicIssueCreateModal } from '@atlassian/universal-create-integrations/gic-issue-create';

import { useUniversalCreateState } from '../../../../context';

export type GicModalContextValue = {
	openGicModal: () => void;
};

const GicModalContext = createContext<GicModalContextValue | null>(null);

export const useGicModal: () => GicModalContextValue = () => {
	const context = useContext(GicModalContext);
	if (context === null) {
		throw new Error('useGicModal must be used within a GicModalProvider');
	}
	return context;
};

export const GicModalProvider = ({ children }: { children: React.ReactNode }) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const { crossAppCreationConfig, product, subProduct } = useUniversalCreateState();

	const cloudId = crossAppCreationConfig.enabled ? crossAppCreationConfig.cloudId : null;

	const openGicModal = useCallback(() => {
		setIsModalOpen(true);
	}, []);

	const handleCloseGicModal = useCallback(() => {
		setIsModalOpen(false);
	}, []);

	const contextValue = useMemo(() => ({ openGicModal }), [openGicModal]);

	return (
		<GicModalContext.Provider value={contextValue}>
			{children}
			{cloudId && (
				<GicIssueCreateModal
					cloudId={cloudId}
					product={product}
					subProduct={subProduct}
					// This should always be true in cross-app creation flow
					isCrossAppCreate={true}
					isOpen={isModalOpen}
					onClose={handleCloseGicModal}
				/>
			)}
		</GicModalContext.Provider>
	);
};
